import React, { useState, useEffect, useRef, KeyboardEvent } from 'react';
import { api } from '@/services/api';
import { CategoryInputProps } from '../../types';
import '../styles.css';
import './styles.css';

const CategoryInput: React.FC<CategoryInputProps> = ({
  value,
  onChange,
  onSelect,
  required = false,
  placeholder = 'Enter category',
  label = 'Category',
  id = 'category',
  className = '',
  disabled = false,
  error,
  testId
}) => {
  const [categories, setCategories] = useState<string[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Fetch all categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setIsLoading(true);
        const data = await api.tasks.getCategories();
        setCategories(data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Filter categories based on input value
  useEffect(() => {
    if (!value.trim()) {
      setFilteredCategories([]);
      return;
    }

    const filtered = categories.filter(category => 
      category.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredCategories(filtered);
    setFocusedIndex(-1);
  }, [value, categories]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
    setShowSuggestions(true);
  };

  const handleSuggestionClick = (category: string) => {
    onChange(category);
    onSelect?.(category);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || filteredCategories.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => 
          prev < filteredCategories.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => 
          prev > 0 ? prev - 1 : filteredCategories.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0) {
          handleSuggestionClick(filteredCategories[focusedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setFocusedIndex(-1);
        break;
    }
  };

  const suggestionsToShow = filteredCategories;
  const inputId = `${id}-input`;
  const listboxId = `${id}-listbox`;

  return (
    <div 
      className={`input-container ${className}`}
      data-testid={testId}
    >
      <label 
        htmlFor={inputId}
        className="input-label"
      >
        {label}
        {required && <span className="required-indicator">*</span>}
      </label>
      <div className="input-wrapper">
        <input
          ref={inputRef}
          type="text"
          id={inputId}
          className={`input-field ${error ? 'has-error' : ''}`}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowSuggestions(true)}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          autoComplete="off"
          aria-invalid={!!error}
          aria-describedby={error ? `${inputId}-error` : undefined}
          aria-expanded={showSuggestions}
          aria-controls={showSuggestions ? listboxId : undefined}
          aria-activedescendant={
            focusedIndex >= 0 ? `${id}-option-${focusedIndex}` : undefined
          }
          role="combobox"
        />
        {isLoading && <div className="loading-indicator" role="status" aria-label="Loading suggestions" />}
        {error && (
          <div 
            id={`${inputId}-error`} 
            className="error-text" 
            role="alert"
          >
            {error}
          </div>
        )}
        {showSuggestions && suggestionsToShow.length > 0 && (
          <div 
            ref={suggestionsRef}
            id={listboxId}
            className="suggestions-container"
            role="listbox"
          >
            {suggestionsToShow.map((category, index) => (
              <div 
                key={category}
                id={`${id}-option-${index}`}
                className={`suggestion-item ${focusedIndex === index ? 'focused' : ''}`}
                onClick={() => handleSuggestionClick(category)}
                onMouseEnter={() => setFocusedIndex(index)}
                role="option"
                aria-selected={focusedIndex === index}
              >
                {category}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryInput; 